import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import * as db from "@/lib/db.server";

/** Intent filters surface "Available today / Telehealth / Accepting new patients" pages.
 * Pages stay noindex until a real provider matches the intent for that specialty+city. */
export type IntentKind = "available_today" | "accepting_new_patients" | "telehealth";

const intentSchema = z
  .object({
    specialty: z.string().min(1).max(100),
    city: z.string().min(1).max(100),
    state: z.string().length(2).optional(),
    kind: z.enum(["available_today", "accepting_new_patients", "telehealth"]),
  })
  .strict();

/** Returns providers in (specialty, city) matching the requested intent.
 * Used by intent route shells to decide indexability + render listings.
 * Public SEO endpoint (no auth) — reads only active, non-deleted providers. */
export const getIntentProviders = createServerFn({ method: "GET" })
  .inputValidator((input) => intentSchema.parse(input))
  .handler(async ({ data }) => {
    const conditions: string[] = [
      "lifecycle_status = 'active'",
      "is_deleted = false",
      "specialty ILIKE $1",
      "city ILIKE $2",
    ];
    const params: unknown[] = [data.specialty, data.city];

    if (data.state) {
      params.push(data.state);
      conditions.push(`state = $${params.length}`);
    }

    if (data.kind === "available_today") {
      // proxy: providers that expose availability days or instant booking
      conditions.push("(accepts_instant_booking = true OR in_app_calendar_active = true)");
    } else if (data.kind === "telehealth") {
      // best-effort: no dedicated column yet; require in_app_calendar_active as proxy
      conditions.push("in_app_calendar_active = true");
    } else if (data.kind === "accepting_new_patients") {
      conditions.push("accepts_instant_booking = true");
    }

    const where = conditions.join(" AND ");

    const rows = await db.query<{
      id: string;
      public_id: string | null;
      name: string | null;
      credentials: string | null;
      specialty: string | null;
      practice: string | null;
      city: string | null;
      state: string | null;
      available_days: any;
      accepts_instant_booking: boolean | null;
      in_app_calendar_active: boolean | null;
    }>(
      `SELECT id, public_id, name, credentials, specialty, practice, city, state, available_days, accepts_instant_booking, in_app_calendar_active
         FROM public.providers
        WHERE ${where}
        LIMIT 50`,
      params,
    );

    const countRow = await db.one<{ count: number }>(
      `SELECT count(*)::int AS count FROM public.providers WHERE ${where}`,
      params,
    );

    return { providers: rows, total: countRow?.count ?? 0 };
  });
